import {
  ExclusionField,
  ExclusionFieldOperator,
  ExclusionRuleConditionInput,
  ExclusionValueShape,
  MAX_CONDITION_VALUES
} from 'models'

// One editable condition row. `values` holds every shape: 1 entry for exactly_one, [low, high] for exactly_two.
export interface DraftCondition {
  key: string
  id?: string
  field_name: string
  predicate_type: string
  values: string[]
}

let keySeq = 0
const nextKey = (): string => `cond-${(keySeq += 1)}`

export const findField = (fields: ExclusionField[], name: string): ExclusionField | undefined =>
  fields.find((f) => f.name === name)

export const findOperator = (
  field: ExclusionField | undefined,
  predicate: string
): ExclusionFieldOperator | undefined => field?.operators.find((o) => o.predicate === predicate)

export const valueShapeOf = (field: ExclusionField | undefined, predicate: string): ExclusionValueShape | undefined =>
  findOperator(field, predicate)?.value_shape

export const makeDraftCondition = (field?: ExclusionField): DraftCondition => ({
  key: nextKey(),
  field_name: field?.name ?? '',
  predicate_type: field?.default_predicate ?? '',
  values: []
})

export const draftFromConditionInput = (c: ExclusionRuleConditionInput): DraftCondition => ({
  key: nextKey(),
  ...(c.id && { id: c.id }),
  field_name: c.field_name,
  predicate_type: c.predicate_type,
  values: (c.predicate_value?.values ?? []).map(String)
})

export const withField = (draft: DraftCondition, field: ExclusionField): DraftCondition => ({
  ...draft,
  field_name: field.name,
  predicate_type: field.default_predicate,
  values: []
})

export const withOperator = (draft: DraftCondition, predicate: string): DraftCondition => ({
  ...draft,
  predicate_type: predicate,
  values: []
})

// Single and bulk select both write `any_` on the record identifier.
export const selectionCondition = (recordIdentifier: string, ids: string[]): ExclusionRuleConditionInput => ({
  field_name: recordIdentifier,
  predicate_type: 'any_',
  predicate_value: { values: ids }
})

const isNumeric = (v: string) => v.trim() !== '' && !Number.isNaN(Number(v))

const rangeIsOrdered = (field: ExclusionField, low: string, high: string): boolean => {
  if (field.type === 'number' && isNumeric(low) && isNumeric(high)) return Number(low) <= Number(high)
  if (field.type === 'timestamp') return low <= high
  return true
}

export type ConditionProblem = 'incomplete' | 'range' | 'tooManyValues'

// A stored condition on a field missing from the fields response is read-only and sent back unchanged.
export const conditionProblem = (c: DraftCondition, fields: ExclusionField[]): ConditionProblem | null => {
  const field = findField(fields, c.field_name)
  if (!field) return c.id ? null : 'incomplete'
  const shape = valueShapeOf(field, c.predicate_type)
  const filled = c.values.filter((v) => v.trim() !== '')
  switch (shape) {
    case 'none':
      return null
    case 'one_or_more':
      if (filled.length === 0) return 'incomplete'
      return filled.length > MAX_CONDITION_VALUES ? 'tooManyValues' : null
    case 'exactly_one':
      return filled.length === 1 && c.values[0]?.trim() ? null : 'incomplete'
    case 'exactly_two':
      if (!c.values[0]?.trim() || !c.values[1]?.trim()) return 'incomplete'
      return rangeIsOrdered(field, c.values[0].trim(), c.values[1].trim()) ? null : 'range'
    default:
      return 'incomplete'
  }
}

const wireValues = (c: DraftCondition, shape: ExclusionValueShape | undefined): string[] => {
  const trimmed = c.values.map((v) => v.trim())
  switch (shape) {
    case 'none':
      return []
    case 'exactly_one':
      return trimmed.slice(0, 1)
    case 'exactly_two':
      return trimmed.slice(0, 2)
    case 'one_or_more':
      return trimmed.filter(Boolean)
    default:
      return c.values
  }
}

export const toConditionInputs = (drafts: DraftCondition[], fields: ExclusionField[]): ExclusionRuleConditionInput[] =>
  drafts.map((c) => ({
    ...(c.id && { id: c.id }),
    field_name: c.field_name,
    predicate_type: c.predicate_type,
    predicate_value: { values: wireValues(c, valueShapeOf(findField(fields, c.field_name), c.predicate_type)) }
  }))

const toWireValue = (v: unknown): string => {
  if (v instanceof Date) return v.toISOString()
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  return String(v)
}

export interface FilterConversion {
  conditions: ExclusionRuleConditionInput[]
  // Record-page column names whose clause cannot become an exclusion condition.
  unsupportedColumns: string[]
}

/**
 * Converts a record-page query filter ({ COLUMN: { filter_predicate: value } }) into exclusion
 * conditions. All or nothing: callers must refuse to build a rule when any column is unsupported.
 */
export const convertFilterToConditions = (
  queryFilter: Record<string, Record<string, unknown> | undefined>,
  fields: ExclusionField[]
): FilterConversion => {
  const conditions: ExclusionRuleConditionInput[] = []
  const unsupported = new Set<string>()
  for (const [column, clause] of Object.entries(queryFilter)) {
    if (!clause) continue
    const field = findField(fields, column)
    for (const [filterPredicate, raw] of Object.entries(clause)) {
      const predicate = field?.filter_conversions[filterPredicate]
      if (!field || !predicate) {
        unsupported.add(column)
        continue
      }
      const values = (Array.isArray(raw) ? raw : [raw]).filter((v) => v != null).map(toWireValue)
      conditions.push({ field_name: field.name, predicate_type: predicate, predicate_value: { values } })
    }
  }
  return { conditions, unsupportedColumns: Array.from(unsupported) }
}

export const unsupportedFieldsMessage = (displayNames: string[]): string =>
  `Exclusion rules don't support ${displayNames.join(
    ', '
  )}. Use a different field or condition to define the exclusion.`
